import {ChangeDetectorRef, Component, forwardRef, Input, OnInit} from '@angular/core';
import {ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR} from '@angular/forms';
import moment from 'moment-timezone';
import {OverlayModule} from '@angular/cdk/overlay';
import {CommonModule} from '@angular/common';

@Component({
  selector: 'core-datepicker',
  standalone: true,
  imports: [ CommonModule, FormsModule, OverlayModule],
  templateUrl: './date-picker.html',
  styleUrl: './date-picker.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatePicker),
      multi: true,
    },
  ],
})
export class DatePicker implements ControlValueAccessor, OnInit {
  @Input() range: boolean = false;
  @Input() placeholder: string = 'Seleccionar fecha';
  @Input() compact: boolean = false;
  @Input() reverse: boolean = false;

  presets = [
    { label: 'Hoy', getRange: () => ({ start: moment(), end: moment() }) },
    { label: 'Próximos 7 días', getRange: () => ({ start: moment(), end: moment().add(7, 'days') }) },
    { label: 'Próximos 30 días', getRange: () => ({ start: moment(), end: moment().add(30, 'days') }) },
    { label: 'Próximos 3 meses', getRange: () => ({ start: moment(), end: moment().add(3, 'months') }) },
    { label: 'Próximos 6 meses', getRange: () => ({ start: moment(), end: moment().add(6, 'months') }) },
    { label: 'Hasta final de año', getRange: () => ({ start: moment(), end: moment().endOf('year') }) },
    { label: 'Último año', getRange: () => ({ start: moment().subtract(1, 'year'), end: moment() }) },
  ];

  isOpen = false;
  isDisabled = false;
  viewDate = moment().startOf('month');
  nextMonthDate = moment().add(1, 'month').startOf('month');

  selectedDate?: moment.Moment;
  startDate?: moment.Moment;
  endDate?: moment.Moment;

  displayValue: string = '';
  daysOfMonth: (moment.Moment | null)[] = [];
  daysOfNextMonth: (moment.Moment | null)[] = [];

  daysOfWeek = ['Do', 'Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sa'];

  constructor(private cdr: ChangeDetectorRef) {}

  onChange = (_: any) => {};

  onTouched = () => {};

  ngOnInit() {
    this.updateMonthViews();
  }

  toggle() {
    if (this.isDisabled) return;
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.cdr.detectChanges();
    }
  }

  selectPreset(preset: any) {
    if (this.isDisabled) return;
    const { start, end } = preset.getRange();
    if (!this.range) {
      this.selectDate(start);
    } else {
      this.startDate = start.startOf('day');
      this.endDate = end.startOf('day');
      this.viewDate = moment(this.startDate).startOf('month');
      this.updateMonthViews();
      this.onChange({
        start: this?.startDate?.toISOString(),
        end: this?.endDate?.toISOString()
      });
      this.isOpen = false;
    }
  }

  updateMonthViews() {
    this.nextMonthDate = moment(this.viewDate).add(1, 'month');
    this.daysOfMonth = this.getDaysInMonth(this.viewDate);
    this.daysOfNextMonth = this.getDaysInMonth(this.nextMonthDate);
    this.updateDisplayValue();
  }

  getDaysInMonth(date: moment.Moment): (moment.Moment | null)[] {
    const start = moment(date).startOf('month');
    const end = moment(date).endOf('month');
    const days: (moment.Moment | null)[] = [];

    // Relleno inicial para alinear el primer día del mes
    for (let i = 0; i < start.day(); i++) {
      days.push(null);
    }

    const current = moment(start);
    while (current <= end) {
      days.push(moment(current));
      current.add(1, 'day');
    }

    return days;
  }

  selectDate(date: moment.Moment | null) {
    if (this.isDisabled) return;
    if (!date) return;

    const day = moment(date).startOf('day');

    if (!this.range) {
      this.selectedDate = day;
      this.updateDisplayValue();
      this.onChange(this.selectedDate.toISOString());
      this.isOpen = false;
    } else {
      if (!this.startDate || (this.startDate && this.endDate)) {
        this.startDate = day;
        this.endDate = undefined;
      } else if (day.isBefore(this.startDate)) {
        this.endDate = moment(this.startDate);
        this.startDate = day;
      } else {
        this.endDate = day;
        this.isOpen = false;
      }
      this.updateDisplayValue();
      if (this.startDate && this.endDate) {
        this.onChange({
          start: this.startDate.toISOString(),
          end: this.endDate.toISOString()
        });
      }
    }
  }

  isSelected(date: moment.Moment | null): boolean {
    if (!date) return false;
    if (!this.range) return !!this.selectedDate && date.isSame(this.selectedDate, 'day');
    return (!!this.startDate && date.isSame(this.startDate, 'day')) ||
      (!!this.endDate && date.isSame(this.endDate, 'day'));
  }

  isInRange(date: moment.Moment | null): boolean {
    if (!date || !this.range || !this.startDate || !this.endDate) return false;
    return date.isBetween(this.startDate, this.endDate, 'day', '[]');
  }

  isToday(date: moment.Moment | null): boolean {
    return !!date && date.isSame(moment(), 'day');
  }

  prevMonth() {
    if (this.isDisabled) return;
    this.viewDate = moment(this.viewDate).subtract(1, 'month').startOf('month');
    this.updateMonthViews();
  }

  nextMonth() {
    if (this.isDisabled) return;
    this.viewDate = moment(this.viewDate).add(1, 'month').startOf('month');
    this.updateMonthViews();
  }

  prevYear() {
    if (this.isDisabled) return;
    this.viewDate = moment(this.viewDate).subtract(1, 'year').startOf('month');
    this.updateMonthViews();
  }

  nextYear() {
    if (this.isDisabled) return;
    this.viewDate = moment(this.viewDate).add(1, 'year').startOf('month');
    this.updateMonthViews();
  }

  getMonthName(date: moment.Moment): string {
    return date.format('MMMM YYYY');
  }

  writeValue(value: any): void {
    if (!value) {
      this.selectedDate = undefined;
      this.startDate = undefined;
      this.endDate = undefined;
    } else if (this.range && value.start && value.end) {
      this.startDate = moment(value.start);
      this.endDate = moment(value.end);
      this.viewDate = moment(this.startDate).startOf('month');
    } else if (!this.range) {
      this.selectedDate = moment(value);
      this.viewDate = moment(this.selectedDate).startOf('month');
    }
    this.updateMonthViews();
    this.cdr.markForCheck();
  }

  registerOnChange(fn: any) { this.onChange = fn; }

  registerOnTouched(fn: any) { this.onTouched = fn; }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    if (isDisabled) this.isOpen = false;
    this.cdr.markForCheck();
  }

  private updateDisplayValue() {
    if (!this.range) {
      this.displayValue = this.selectedDate ? this.selectedDate.format('DD/MM/YYYY') : '';
    } else {
      if (this.startDate && this.endDate) {
        this.displayValue = `${this.startDate.format('DD/MM/YYYY')} - ${this.endDate.format('DD/MM/YYYY')}`;
      } else {
        this.displayValue = this.startDate ? this.startDate.format('DD/MM/YYYY') : '';
      }
    }
  }
}
